import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronRight, Search, X, ChevronLeft } from "lucide-react";
import { CATEGORIES } from "../utils/Categories";

export default function CategorySelect({ value = [], onChange }) {
  const [show, setShow] = useState(false);

  // derive labels from current value
  const activePath = useMemo(() => findPath(CATEGORIES, value) || [], [value]);
  const pathLabels = activePath.length
    ? activePath.map((c) => c.name).join(" › ")
    : "Select…";

  return (
    <div className="relative">
      <Label>Category</Label>
      <button
        type="button"
        className="input text-left flex justify-between items-center"
        onClick={() => setShow(true)}
      >
        <span className="truncate">{pathLabels}</span>
        <ChevronRight size={16} />
      </button>

      {show &&
        createPortal(
          <CategoryPickerSheet
            tree={CATEGORIES}
            initialPath={activePath}
            onSelect={(ids) => {
              onChange(ids);
              setShow(false);
            }}
            onClose={() => setShow(false)}
          />,
          document.body
        )}
    </div>
  );
}

/* ---------------- Full-screen sheet used on ALL devices ---------------- */
function CategoryPickerSheet({ tree = [], initialPath = [], onSelect, onClose }) {
  const [query, setQuery] = useState("");
  const [path, setPath] = useState(initialPath);

  // esc to close
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const leaves = useMemo(() => flattenLeaves(tree), [tree]);
  const results = useMemo(() => {
    const q = norm(query);
    if (!q) return [];
    return leaves.filter((l) => l.norm.includes(q)).slice(0, 60);
  }, [query, leaves]);

  const currentNodes = useMemo(() => {
    let nodes = tree;
    for (const n of path) nodes = n.children || [];
    return nodes || [];
  }, [path, tree]);

  const pathLabels = path.length ? path.map((c) => c.name).join(" › ") : "Select…";

  const goBack = () => {
    if (query) return setQuery("");
    if (path.length) return setPath((p) => p.slice(0, -1));
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-white flex flex-col"
      style={{
        height: "100svh",               // modern mobile browsers
        touchAction: "pan-y",
        WebkitOverflowScrolling: "touch",
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Category menu"
    >
      {/* Header */}
      <div className="sticky top-0 z-10 border-b bg-white">
        <div className="flex items-center gap-2 px-3 py-2">
          <button
            type="button"
            onClick={goBack}
            className="p-1 -ml-1 rounded hover:bg-gray-100"
            aria-label={path.length || query ? "Back" : "Close"}
          >
            <ChevronLeft size={18} />
          </button>
          <div className="text-sm font-medium">Choose Category</div>
          <div className="ml-auto" />
        </div>
        <div className="px-3 pb-2">
          <SearchBar query={query} setQuery={setQuery} />
          {!!path.length && !query && <Breadcrumb path={path} />}
        </div>
      </div>

      {/* Content */}
      <div
        className="flex-1 overflow-y-auto"
        style={{ WebkitOverflowScrolling: "touch", overscrollBehavior: "contain" }}
      >
        {query ? (
          <ResultList
            results={results}
            onPick={(fullPath) => onSelect(fullPath.map((n) => n.id))}
          />
        ) : (
          <ul className="divide-y">
            {currentNodes.map((n) => {
              const isLeaf = !n.children?.length;
              return (
                <li key={n.id}>
                  <button
                    type="button"
                    className="w-full text-left px-4 py-3 text-sm flex items-center justify-between hover:bg-gray-50"
                    onClick={() =>
                      isLeaf
                        ? onSelect([...path, n].map((x) => x.id))
                        : setPath((p) => [...p, n])
                    }
                    title={n.name}
                  >
                    <span className="truncate">{n.name}</span>
                    {isLeaf ? null : <ChevronRight size={16} className="text-gray-400" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Footer */}
      <div className="sticky bottom-0 border-t bg-white p-2">
        <div className="flex justify-between items-center text-xs">
          <button type="button" className="text-gray-600 hover:underline" onClick={() => onSelect([])}>
            Clear
          </button>
          {!!path.length && (
            <span className="text-gray-500 truncate max-w-[75%]">Selected: {pathLabels}</span>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- tiny bits ---------- */
function SearchBar({ query, setQuery }) {
  return (
    <div className="border rounded-lg flex items-center gap-2 bg-white px-2 py-1">
      <Search size={16} className="text-gray-400" />
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search categories…"
        className="w-full outline-none text-sm"
        inputMode="search"
      />
      {query && (
        <button
          type="button"
          className="text-gray-400 hover:text-gray-600"
          onClick={() => setQuery("")}
          aria-label="Clear search"
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
}
function Breadcrumb({ path = [] }) {
  if (!path.length) return null;
  return <div className="mt-1 text-[11px] text-gray-500 truncate">{path.map((n) => n.name).join(" › ")}</div>;
}
function Label({ children }) {
  return <label className="text-sm font-medium text-gray-700 mb-1 block">{children}</label>;
}
function norm(s) {
  return (s || "").toString().normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}
function findPath(tree, ids) {
  const path = [];
  let nodes = tree;
  for (const rawId of ids || []) {
    const id = String(rawId);
    const n = nodes?.find((x) => String(x.id) === id);
    if (!n) return null;
    path.push(n);
    nodes = n.children;
  }
  return path;
}
function flattenLeaves(tree) {
  const out = [];
  const walk = (nodes, path = []) => {
    for (const n of nodes || []) {
      const np = [...path, n];
      if (!n.children?.length) {
        const full = np.map((x) => x.name).join(" ");
        out.push({ leaf: n, path: np, norm: norm(full) });
      } else {
        walk(n.children, np);
      }
    }
  };
  walk(tree);
  return out;
}

function ResultList({ results, onPick }) {
  if (!results.length) return <p className="p-3 text-sm text-gray-500">No matches.</p>;
  return (
    <ul className="divide-y">
      {results.map((r) => (
        <li key={r.leaf.id}>
          <button
            type="button"
            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
            onClick={() => onPick(r.path)}
            title={r.path.map((n) => n.name).join(" › ")}
          >
            <span className="font-medium">{r.leaf.name}</span>
            <span className="text-gray-500"> — {r.path.slice(0, -1).map((n) => n.name).join(" › ")}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}
